import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { logo } from '@clofo/ui/images'
import { useTranslation } from '@clofo/i18n'
import { usePageTitle } from '@clofo/ui/hooks'
import { LoadingSpinner } from '@clofo/ui/loading-spinner'
import { clofoApi } from '@clofo/data/api'
import { ResetPasswordForm } from '../components/reset-password-form'

/**
 * Landing page for the password-reset email link: /passwords/reset?token=…&uid=…. The uid/token
 * pair is validated against the backend before the form is shown; an invalid or expired token
 * redirects to the token-error page.
 */
export const ResetPasswordPage = () => {
  const { t } = useTranslation()
  usePageTitle(t('login.reset.title'))
  const navigate = useNavigate()

  // The app uses hash routing, so the emailed link's params live after the # and are only
  // readable via the router — window.location.search would be empty.
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const uid = searchParams.get('uid')

  const [isTokenValid, setIsTokenValid] = useState(false)

  useEffect(() => {
    if (!token || !uid) return

    let cancelled = false
    clofoApi.auth
      .checkResetToken({ uidb64: uid, token })
      .then(() => {
        if (!cancelled) setIsTokenValid(true)
      })
      .catch(() => {
        if (!cancelled) navigate('/passwords/token-error', { replace: true })
      })
    return () => {
      cancelled = true
    }
  }, [token, uid, navigate])

  if (!token || !uid) return <Navigate to='/' replace />

  return (
    <main className='flex flex-col bg-background items-center justify-center'>
      <img src={logo} alt='Closing Folders' className='my-13 h-15' />
      <div className='w-full max-w-xl px-4'>
        <h1 className='text-xl font-semibold mb-6'>{t('login.reset.title')}</h1>
        {isTokenValid ? (
          <ResetPasswordForm uidb64={uid} token={token} />
        ) : (
          <div className='flex justify-center py-8' aria-busy='true' aria-live='polite'>
            <LoadingSpinner />
          </div>
        )}
      </div>
    </main>
  )
}
